import { useQuery } from '@tanstack/react-query';
import type { MpCounterDTO, PaymentIntentDTO } from '@bifeai/shared';
import { Button, Callout, Field, Select, Spinner } from '@bifeai/ui';
import { CircleCheck, RotateCw, Smartphone, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';

/** Qué cobros de Mercado Pago hay en esta sucursal (QR, posnets Point). */
export function useMpCounter(branchId: string | null) {
  return useQuery({
    queryKey: ['mp', 'counter', branchId],
    queryFn: () => api.get<MpCounterDTO>(`/mp/counter?branchId=${branchId}`),
    enabled: !!branchId,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Cobro con Mercado Pago: QR con el importe exacto (en pantalla o el impreso de la caja) o envío al posnet Point.
 * Espera la confirmación de Mercado Pago y avisa con `onPaid`. Si se cierra sin pagar, el cobro se cancela.
 */
export function MpCharge({
  kind,
  amountCents,
  branchId,
  terminals,
  onPaid,
}: {
  kind: 'QR' | 'POINT';
  amountCents: number;
  branchId: string;
  terminals: MpCounterDTO['terminals'];
  onPaid: (intentId: string) => void;
}) {
  const [terminalId, setTerminalId] = useState(terminals[0]?.terminalId ?? '');
  const [intentId, setIntentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const paidRef = useRef(false);

  const intent = useQuery({
    queryKey: ['mp', 'intent', intentId],
    queryFn: () => api.get<PaymentIntentDTO>(`/mp/intents/${intentId}`),
    enabled: !!intentId,
    refetchInterval: (q) => (q.state.data && q.state.data.status !== 'CREATED' ? false : 2000),
    staleTime: 0,
  });
  const data = intent.data;

  async function start() {
    setStarting(true);
    setError(null);
    try {
      const r = await api.post<PaymentIntentDTO>('/mp/intents', { kind, amountCents, branchId, terminalId: kind === 'POINT' ? terminalId : null });
      if (r.status === 'FAILED') setError(r.error ?? 'Mercado Pago no pudo crear el cobro.');
      else setIntentId(r.id);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setStarting(false);
    }
  }

  async function cancel() {
    if (!intentId) return;
    const r = await api.post<PaymentIntentDTO>(`/mp/intents/${intentId}/cancel`).catch(() => null);
    if (r?.status === 'PAID') return; // el cliente ya había pagado: lo toma el aviso de pago
    setIntentId(null);
  }

  // QR: se genera solo. Point con un solo posnet: se manda solo.
  useEffect(() => {
    if (kind === 'QR' || terminals.length === 1) void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.status === 'PAID' && !paidRef.current) {
      paidRef.current = true;
      onPaid(data.id);
    }
  }, [data, onPaid]);

  // Si se cierra la ventana sin pagar, el cobro se cancela en Mercado Pago.
  const pendingRef = useRef<string | null>(null);
  pendingRef.current = intentId && data?.status !== 'PAID' ? intentId : null;
  useEffect(
    () => () => {
      if (pendingRef.current) void api.post(`/mp/intents/${pendingRef.current}/cancel`).catch(() => undefined);
    },
    [],
  );

  if (!intentId) {
    return (
      <div className="bf-mp-charge">
        {kind === 'POINT' && terminals.length > 1 && (
          <Field label="Posnet Point">
            <Select value={terminalId} onChange={(e) => setTerminalId(e.target.value)}>
              {terminals.map((t) => (
                <option key={t.terminalId} value={t.terminalId}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
        {starting ? (
          <p className="bf-mp-wait">
            <Spinner /> {kind === 'QR' ? 'Generando el QR…' : 'Enviando el importe al posnet…'}
          </p>
        ) : (
          <Button variant="primary" icon={kind === 'QR' ? <RotateCw /> : <Smartphone />} onClick={() => void start()}>
            {kind === 'QR' ? (error ? 'Reintentar' : 'Generar QR') : `Enviar ${formatMoney(amountCents)} al posnet`}
          </Button>
        )}
      </div>
    );
  }

  const status = data?.status ?? 'CREATED';
  return (
    <div className="bf-mp-charge">
      {status === 'PAID' ? (
        <Callout tone="success">
          <strong>¡Pago aprobado por Mercado Pago!</strong> Cerrando la venta…
        </Callout>
      ) : status === 'CREATED' ? (
        <>
          {kind === 'QR' && data?.qrDataUrl && (
            <div className="bf-mp-qr">
              <img src={data.qrDataUrl} alt={`QR de Mercado Pago por ${formatMoney(amountCents)}`} />
              <div>
                <strong className="bf-num">{formatMoney(amountCents)}</strong>
                <p>Que el cliente escanee este QR con Mercado Pago o cualquier billetera.</p>
                {data.qrImageUrl && <p className="bf-hint">También puede escanear el QR impreso de la caja: le aparece este mismo importe.</p>}
              </div>
            </div>
          )}
          {kind === 'POINT' && (
            <p>
              El importe ya está en el posnet <strong>{terminals.find((t) => t.terminalId === terminalId)?.name ?? 'Point'}</strong>: el cliente paga
              con tarjeta o QR ahí.
            </p>
          )}
          <p className="bf-mp-wait">
            <Spinner /> Esperando el pago… se confirma solo.
          </p>
          <div>
            <Button size="sm" variant="ghost" icon={<X />} onClick={() => void cancel()}>
              Cancelar cobro
            </Button>
          </div>
        </>
      ) : (
        <>
          <Callout tone="warning">
            {status === 'CANCELED'
              ? 'Cobro cancelado.'
              : status === 'EXPIRED'
                ? 'El cobro venció sin pagarse.'
                : (data?.error ?? 'El pago no se pudo hacer.')}
          </Callout>
          <div>
            <Button size="sm" icon={<RotateCw />} onClick={() => setIntentId(null)}>
              Volver a cobrar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** Transferencia: busca en Mercado Pago si ya entró la plata (no alcanza con una captura). */
export function VerifyTransfer({
  amountCents,
  branchId,
  verified,
  onVerified,
}: {
  amountCents: number;
  branchId: string;
  verified: PaymentIntentDTO | null;
  onVerified: (i: PaymentIntentDTO) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (verified) {
    return (
      <p className="bf-text-success" style={{ margin: 0 }}>
        <CircleCheck size={16} aria-hidden style={{ verticalAlign: '-3px' }} /> Transferencia verificada en Mercado Pago
        {verified.payer ? ` · de ${verified.payer}` : ''}.
      </p>
    );
  }
  return (
    <div className="bf-mp-charge">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          size="sm"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              onVerified(await api.post<PaymentIntentDTO>('/mp/transfers/verify', { amountCents, branchId }));
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          Verificar en Mercado Pago
        </Button>
        <span className="bf-hint">Opcional: confirma que entró la plata en la cuenta de Mercado Pago.</span>
      </div>
      {error && (
        <p className="bf-hint bf-text-warning" style={{ margin: 0 }}>
          {error}
        </p>
      )}
    </div>
  );
}
